import { Suspense } from "react";
import { ROUTES } from "../constants";
import { Routes, Route } from "react-router-dom";
import { PrivateRoute } from "../context/routes/privateRoutes";

// Other imports...
import Wallets from "../../pages/wallets";
import Onboarding from "../../components/onboarding";
import { isLoggedIn } from "../context/authContext";
import {
  canAccessSettings as canAccessSettingsPage,
  canManageAutoCashout as canManageAutoCashoutPage,
  StaffTier,
} from "../auth/oidc";
import ContractNotFound from "../../pages/contract/notFound";
import UsersManagement from "../../pages/usersManagement";
import Organizations from "../../pages/organizations";
import Login from "../../pages/authentication/login";
import Payment from "../../pages/payment";
import ContractsList from "../../pages/contract/list";
import ContractRoute from "../context/routes/contractRoues";
import UserProfile from "../../pages/userProfile";
import HomeStats from "../../pages/homeStats";
import UserStats from "../../pages/userStats";
import Settings from "../../pages/settings";
import AutoCashoutSettings from "../../pages/autoCashoutSettings";
import ContractProvider from "../../providers/contract/contract.provider";
import OrganstionProvider from "../../providers/organisation/organisation.provider";
import Settlements from "../../pages/settlements";
import Cashouts from "../../pages/cashouts";
import Alerts from "../../pages/alerts";
import CashoutDetails from "../../pages/cashouts/CashoutDetails";
import Withdraws from "../../pages/withdraws/Withdraws";
import WithdrawDetails from "../../pages/withdraws/WithdrawDetails";
import CompletedTransfers from "../../pages/completedTransfers/CompletedTransfers";
import CompletedTransfersV2 from "../../pages/completedTransfersV2/CompletedTransfersV2";
import Advertisement from "../../pages/advertisment";
import MultiCashouts from "../../pages/multiCashouts";
import MultiCashoutDetails from "../../pages/multiCashouts/MultiCashoutDetails";
import ContractsList_v2 from "../../pages/contract/list_v2";
import OrganizationDetails from "../../pages/organizations/OrganizationDetails";

interface Props {
  isAdmin: boolean;
  canAct?: boolean;
  tier?: StaffTier;
  adminOrgEnabled?: boolean;
}

export const IndexRouter = ({ isAdmin, canAct, adminOrgEnabled }: Props) => {
  const canAccessSettings = canAccessSettingsPage();
  const canManageAutoCashout = canManageAutoCashoutPage();
  return (
    <Suspense fallback={<div />}>
      <Routes>
        {/* Public and private routes */}
        <Route
          path={ROUTES.HOME}
          element={isLoggedIn() ? <HomeStats /> : <Login />}
        />
        <Route path={ROUTES.ONBOARDING} element={<Onboarding />} />
        <Route
          path={ROUTES.CONTRACT_NOT_FOUND}
          element={<ContractNotFound />}
        />
        <Route path={ROUTES.CONTRACTS_LIST} element={<PrivateRoute />}>
          <Route path={ROUTES.CONTRACTS_LIST} element={<ContractsList />} />
        </Route>
        <Route path={ROUTES.CONTRACTS_LIST_V2} element={<PrivateRoute />}>
          <Route
            path={ROUTES.CONTRACTS_LIST_V2}
            element={<ContractsList_v2 />}
          />
        </Route>
        <Route path={ROUTES.SETTLEMENTS} element={<PrivateRoute />}>
          <Route path={ROUTES.SETTLEMENTS} element={<Settlements />} />
        </Route>
        <Route path={ROUTES.CASHOUTS} element={<PrivateRoute />}>
          <Route path={ROUTES.CASHOUTS} element={<Cashouts />} />
        </Route>
        <Route path={ROUTES.MULTI_CASHOUTS} element={<PrivateRoute />}>
          <Route path={ROUTES.MULTI_CASHOUTS} element={<MultiCashouts />} />
        </Route>
        <Route path={ROUTES.WITHDRAWS} element={<PrivateRoute />}>
          <Route path={ROUTES.WITHDRAWS} element={<Withdraws />} />
        </Route>
        <Route path={ROUTES.COMPLETED_TRANSFERS} element={<PrivateRoute />}>
          <Route
            path={ROUTES.COMPLETED_TRANSFERS}
            element={<CompletedTransfers />}
          />
        </Route>
        <Route path={ROUTES.COMPLETED_TRANSFERS_V2} element={<PrivateRoute />}>
          <Route
            path={ROUTES.COMPLETED_TRANSFERS_V2}
            element={<CompletedTransfersV2 />}
          />
        </Route>
        <Route path={ROUTES.ALERTS} element={<PrivateRoute />}>
          <Route path={ROUTES.ALERTS} element={<Alerts />} />
        </Route>
        <Route path={ROUTES.ADVERTISEMENT} element={<PrivateRoute />}>
          <Route path={ROUTES.ADVERTISEMENT} element={<Advertisement />} />
        </Route>
        <Route path={ROUTES.HOME} element={<PrivateRoute />}>
          <Route path={ROUTES.PAYMENT} element={<Payment />} />
        </Route>
        <Route path={ROUTES.HOME} element={<PrivateRoute />}>
          <Route path={ROUTES.STAT_USER_ID} element={<UserStats />} />
        </Route>
        <Route path={ROUTES.CONTRACT_DETAILS} element={<PrivateRoute />}>
          <Route
            path={ROUTES.CONTRACT_DETAILS}
            element={
              <ContractProvider>
                <ContractRoute />
              </ContractProvider>
            }
          />
        </Route>
        <Route path={ROUTES.CASHOUT_DETAILS} element={<PrivateRoute />}>
          <Route
            path={ROUTES.CASHOUT_DETAILS}
            element={
              <ContractProvider>
                <CashoutDetails />
              </ContractProvider>
            }
          />
        </Route>
        <Route path={ROUTES.MULTI_CASHOUT_DETAILS} element={<PrivateRoute />}>
          <Route
            path={ROUTES.MULTI_CASHOUT_DETAILS}
            element={<MultiCashoutDetails />}
          />
        </Route>
        <Route path={ROUTES.WITHDRAW_DETAILS} element={<PrivateRoute />}>
          <Route path={ROUTES.WITHDRAW_DETAILS} element={<WithdrawDetails />} />
        </Route>

        {canAccessSettings && (
          <Route path={ROUTES.SETTINGS} element={<PrivateRoute />}>
            <Route path={ROUTES.SETTINGS} element={<Settings />} />
          </Route>
        )}

        {canManageAutoCashout && (
          <Route path={ROUTES.AUTO_CASHOUT_SETTINGS} element={<PrivateRoute />}>
            <Route
              path={ROUTES.AUTO_CASHOUT_SETTINGS}
              element={<AutoCashoutSettings />}
            />
          </Route>
        )}

        <Route path={ROUTES.USERS_MANAGEMENT} element={<PrivateRoute />}>
          <Route
            path={ROUTES.USERS_MANAGEMENT}
            element={<UsersManagement isAdmin={isAdmin} />}
          />
        </Route>

        {/* Admin Routes */}
        {isAdmin && (
          <>
            <Route path={ROUTES.HOME} element={<PrivateRoute />}>
              <Route path={ROUTES.PAYMENT} element={<Payment />} />
            </Route>
            <Route path={ROUTES.HOME} element={<PrivateRoute />}>
              <Route path={ROUTES.STAT_USER_ID} element={<UserStats />} />
            </Route>
            <Route path={ROUTES.HOME} element={<PrivateRoute />}>
              <Route path={ROUTES.PROFILE} element={<UserProfile />} />
            </Route>
            <Route path={ROUTES.PAYMENTS} element={<PrivateRoute />}>
              <Route path={ROUTES.PAYMENTS} element={<Wallets />} />
            </Route>

            {adminOrgEnabled && (
              <Route path={ROUTES.ORGANIZATIONS} element={<PrivateRoute />}>
                <Route
                  path={ROUTES.ORGANIZATIONS}
                  element={
                    <OrganstionProvider key="organizations">
                      <Organizations />
                    </OrganstionProvider>
                  }
                />
              </Route>
            )}
            {adminOrgEnabled && (
              <Route
                path={ROUTES.ORGANIZATIONS_DETAILS}
                element={<PrivateRoute />}>
                <Route
                  path={ROUTES.ORGANIZATIONS_DETAILS}
                  element={
                    <OrganstionProvider key="organization-details">
                      <OrganizationDetails />
                    </OrganstionProvider>
                  }
                />
              </Route>
            )}
            {/* Distinct keys: these routes render the same components in the same
                tree position as the Organizations routes, so without them React
                reuses the provider (and its store, created with the other page's
                `serviceProvidersOnly`) when navigating between the two tabs. */}
            <Route path={ROUTES.SERVICE_PROVIDERS} element={<PrivateRoute />}>
              <Route
                path={ROUTES.SERVICE_PROVIDERS}
                element={
                  <OrganstionProvider
                    key="service-providers"
                    serviceProvidersOnly>
                    <Organizations serviceProvidersOnly />
                  </OrganstionProvider>
                }
              />
            </Route>
            <Route
              path={ROUTES.SERVICE_PROVIDERS_DETAILS}
              element={<PrivateRoute />}>
              <Route
                path={ROUTES.SERVICE_PROVIDERS_DETAILS}
                element={
                  <OrganstionProvider key="service-provider-details">
                    <OrganizationDetails isServiceProvider />
                  </OrganstionProvider>
                }
              />
            </Route>
          </>
        )}
      </Routes>
    </Suspense>
  );
};
